// pipe: P3.4 (track D) — driver Trip-Log container. Wires the Bearer-authed
// DriverErpService (via the driver context hook) to the pure TripLogView. All
// the fetch/state/effect + payload assembly lives HERE, never in the view (the
// Vitest-tested seam).
//
// Payload discipline: only NON-EMPTY fields ship (the server merges over the
// stored row — an untouched field never clears anything); datetime-local values
// convert to RFC-3339 instants; numbers parse client-side so the typed server
// edge receives real numbers.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { TripLogInput, TripLogRecord } from '../services/driver-erp';
import { useDriverErp } from '../services/driver-erp-provider';
import { TripLogView, type TripFormState } from './trip-log-view';

const EMPTY_FORM: TripFormState = {
  crew_size: '',
  depart_facility_ts: '',
  arrive_customer_ts: '',
  depart_customer_ts: '',
  arrive_facility_ts: '',
  odometer_start: '',
  odometer_dock: '',
  odometer_return: '',
  seal_no: '',
  pallet_count: '',
  notes: '',
};

/** RFC-3339 (server) → `datetime-local` input value (minute precision, local). */
function toLocalInput(v: unknown): string {
  if (typeof v !== 'string') {
    return '';
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** `datetime-local` input value → RFC-3339 instant (undefined when blank/bad). */
function toInstant(v: string): string | undefined {
  if (!v.trim()) {
    return undefined;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toNum(v: string): number | undefined {
  if (!v.trim()) {
    return undefined;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function formFromTrip(trip: TripLogRecord | null): TripFormState {
  if (!trip) {
    return EMPTY_FORM;
  }
  const d = trip.data;
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const n = (k: string) => (typeof d[k] === 'number' ? String(d[k]) : '');
  return {
    crew_size: n('crew_size'),
    depart_facility_ts: toLocalInput(d.depart_facility_ts),
    arrive_customer_ts: toLocalInput(d.arrive_customer_ts),
    depart_customer_ts: toLocalInput(d.depart_customer_ts),
    arrive_facility_ts: toLocalInput(d.arrive_facility_ts),
    odometer_start: n('odometer_start'),
    odometer_dock: n('odometer_dock'),
    odometer_return: n('odometer_return'),
    seal_no: s('seal_no'),
    pallet_count: n('pallet_count'),
    notes: s('notes'),
  };
}

export function TripLog() {
  const erp = useDriverErp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<TripFormState>(EMPTY_FORM);
  const [saved, setSaved] = useState<TripLogRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    erp
      .getTrip(id)
      .then(r => {
        if (!cancelled) {
          setSaved(r.trip);
          setForm(formFromTrip(r.trip));
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [erp, id]);

  const onField = (key: keyof TripFormState, value: string) => {
    setSavedNotice(false);
    setForm(f => ({ ...f, [key]: value }));
  };

  const onSave = () => {
    if (!id) {
      return;
    }
    const body: TripLogInput = {
      crew_size: toNum(form.crew_size),
      depart_facility_ts: toInstant(form.depart_facility_ts),
      arrive_customer_ts: toInstant(form.arrive_customer_ts),
      depart_customer_ts: toInstant(form.depart_customer_ts),
      arrive_facility_ts: toInstant(form.arrive_facility_ts),
      odometer_start: toNum(form.odometer_start),
      odometer_dock: toNum(form.odometer_dock),
      odometer_return: toNum(form.odometer_return),
      seal_no: form.seal_no.trim() || undefined,
      pallet_count: toNum(form.pallet_count),
      notes: form.notes.trim() || undefined,
    };
    setSaving(true);
    setSaveError(null);
    setSavedNotice(false);
    erp
      .saveTrip(id, body)
      .then(trip => {
        setSaved(trip);
        setForm(formFromTrip(trip));
        setSavedNotice(true);
      })
      .catch(() => setSaveError('trip-save-failed'))
      .finally(() => setSaving(false));
  };

  return (
    <TripLogView
      loading={loading}
      error={error}
      form={form}
      onField={onField}
      saved={saved}
      onSave={onSave}
      saving={saving}
      saveError={saveError}
      savedNotice={savedNotice}
      onBack={() => navigate(-1)}
    />
  );
}
